import React, { JSX, useEffect, useRef, useState } from 'react';
import getOsfViewportSize from 'assets/utils/getOsfViewportSize';
import { useRouter } from 'next/router';
import ProviderFilters from './filters/ProviderFilters';
import ProvidersSearchResults from './ProvidersSearchResults';
import ControlToggles from '../shared/ControlToggles';
import ResultsControls from '../shared/ResultsControls';
import {
  AddFilterType,
  DataFacet,
  FacetQueryType,
  FacetType,
  FacetValue,
  FilterPanelType,
  OnQueryInputChangeType,
  ProviderDefaultsType,
  ProviderFilterOptionsType,
  ProviderFilterPanelsType,
  ProviderFiltersType,
  RemoveFilterType,
  ReplaceFilterType,
  ResetFilterToDefaultType,
  SetActiveFilterPanelType,
  SetControlPanelType,
  ToggleMobileControlsType,
  UpdateFilterType,
  UpdateQueryType,
  SuperSpecialty,
} from 'components/types/FilterTypes';
import { ProviderFields } from 'components/types/MapTypes';
import Axios from 'axios';
import {
  getProvidersSearchSrc,
  getResultsCountText,
  getSearchWidget,
} from 'lib/util';
import { ResultsSkeleton } from 'src/skeletons/ResultsSkeleton';
import HeroComponent from 'components/common/HeroComponent';
import SearchBar from '../shared/filters/SearchBar';
import { gql } from 'graphql-request';
import Constants from 'assets/utils/constant';

type ProviderSearchState = {
  activeFilterPanel: string;
  apiError: boolean;
  query: string;
  queryInputValue: string;
  page: number;
  pageSize: number;
  locationsPageSize: number;
  paginationSize: number;
  results: Array<ProviderFields>;
  count: number;
  sort: string;
  facets: Array<FacetType>;
  filters: ProviderFilterOptionsType;
  selectedFilters: ProviderFiltersType;
  selectedFacets: any;
  providerFilterPanels: Array<FilterPanelType>;
  locationFilterPanels: Array<FilterPanelType>;
  mobileControlsOpen: boolean;
  controlPanel: string;
  loading: boolean;
  geolocating: boolean;
};

type NonDefaultParams = {
  [key: string]: string | Array<string> | number | boolean | undefined;
};

type SearchQuery = {
  content: {};
  limit: number;
  offset: number;
  facet: {
    all: boolean;
    max: number;
    types: [
      {
        name: string;
        max: number;
        filter: {
          type: string;
          values: Array<string>;
        };
        sort: {
          name: string;
          order: string;
        };
      },
    ];
  };
  rule: {
    boost: [
      {
        weight: number;
        filter: {
          type: string;
          name: string;
        };
      },
    ];
    bury: {
      filter: {
        type: string;
        name: string;
        value: string;
      };
    };
  };
};

type TypeObject = {
  name: string;
  max?: number;
  filter?: {
    type: string;
    values: Array<string>;
  };
  sort: {
    name: string;
    order: string;
  };
  after?: string;
};

type LatLng = {
  lat: number;
  lng: number;
};

type NormalizedAddress = {
  city?: string;
  state?: string;
  zip?: string;
  label?: string;
};

const filterDefaults: ProviderDefaultsType = {
  availability: [],
  acceptingnewpatients: [],
  contracted: false,
  credentials: [],
  distance: '10',
  facility_services: [],
  facility_types: [],
  gender: [],
  languages: [],
  locationtypes: [],
  locationnames: [],
  onlinescheduling: [],
  PublishToLocal: 'true',
  query: '',
  ratings: 'No preference',
  sort: 'BEST_MATCH',
  sortfield: [],
  specscombo: [],
  page: 1,
};

const pageSizeOptions = [10, 20];

const initialState: ProviderSearchState = {
  activeFilterPanel: '',
  apiError: false,
  query: filterDefaults.query,
  queryInputValue: filterDefaults.query,
  page: filterDefaults.page,
  pageSize: pageSizeOptions[0],
  locationsPageSize: 10, // Remove dynamic viewport check
  paginationSize: 10,
  results: [],
  count: 0,
  sort: filterDefaults.sort,
  facets: [],
  filters: {
    acceptingnewpatients: [],
    availability: [],
    distance: '10',
    gender: [],
    onlinescheduling: [],
    languages: [],
    locationnames: [],
    specscombo: [],
    PublishToLocal: 'true',
  },
  selectedFilters: {
    availability: Array<string>(),
    acceptingnewpatients: Array<string>(),
    distance: '10',
    gender: Array<string>(),
    languages: Array<string>(),
    latitude: '',
    location: '',
    longitude: '',
    locationnames: Array<string>(),
    onlinescheduling: Array<string>(),
    sortfield: Array<string>(),
    specscombo: Array<string>(),
    PublishToLocal: 'true',
  },
  selectedFacets: {
    acceptingnewpatients: Array<string>(),
    availability: Array<string>(),
    gender: Array<string>(),
    languages: Array<string>(),
    locationnames: Array<string>(),
    onlinescheduling: Array<string>(),
    sortfield: Array<string>(),
    specscombo: Array<string>(),
  },
  providerFilterPanels: [
    {
      name: 'Search',
      label: 'Enter name, specialty, or condition',
      icon: 'search',
      id: 'search',
      active: false,
    },
    {
      name: 'Location',
      label: 'City or ZIP',
      id: 'location',
      active: false,
    },
    {
      name: 'Availability',
      id: 'availability',
      active: false,
    },
    {
      name: 'Specialty',
      id: 'specscombo',
      active: false,
    },
    {
      name: 'Offices',
      id: 'locationnames',
      active: false,
    },
    {
      name: 'Gender',
      id: 'gender',
      active: false,
    },
    {
      name: 'Languages Spoken',
      id: 'languages',
      active: false,
    },
  ],
  locationFilterPanels: [
    {
      name: 'Search',
      label: 'name, city or address',
      icon: 'search',
      id: 'search',
      active: false,
    },
    {
      name: 'Location',
      label: 'City or ZIP',
      id: 'location',
      active: false,
    },
    {
      name: 'Languages Spoken',
      id: 'languages',
      active: false,
    },
    {
      name: 'Location Type',
      id: 'locationType',
      active: false,
    },
  ],
  mobileControlsOpen: false,
  controlPanel: '', // Remove dynamic viewport check
  loading: true,
  geolocating: false,
};

const ProviderSearch = (): JSX.Element => {
  const [searchState, setSearchState] = useState({ ...initialState });
  const [allFacets, setAllFacets] = useState<Array<FacetType>>([]);
  const [allFacetsReady, setAllFacetsReady] = useState(false);
  const [superSpecialties, setSuperSpecialties] = useState<
    Array<SuperSpecialty>
  >([]);
  const currentReq = useRef(0);
  const searchType: string = 'providers';

  const router = useRouter();
  const { query: routerQuery } = router;
  const lowercaseQuery = Object.fromEntries(
    Object.entries(routerQuery).map(([key, value]) => [
      key.toLowerCase(),
      value,
    ])
  );

  let selectedFacetsCount = Object.values(searchState.selectedFacets).flat()
    .length;

  const param = (a: any) => {
    const s: any[] = [],
      brack = /\[\]$/,
      isArray = function (obj: any) {
        return Object.prototype.toString.call(obj) === '[object Array]';
      },
      add = function (k: any, v: any) {
        v =
          typeof v === 'function'
            ? v()
            : v === null
              ? ''
              : v === undefined
                ? ''
                : v;
        s[s.length] = encodeURIComponent(k) + '=' + encodeURIComponent(v);
      },
      buildParams = function (prefix: any, obj: any) {
        let i, len, key;

        if (prefix) {
          if (isArray(obj)) {
            for (i = 0, len = obj.length; i < len; i++) {
              if (brack.test(prefix)) {
                add(prefix, obj[i]);
              } else {
                buildParams(
                  prefix + '[' + (typeof obj[i] === 'object' ? i : '') + ']',
                  obj[i]
                );
              }
            }
          } else if (obj && String(obj) === '[object Object]') {
            for (key in obj) {
              buildParams(prefix + '[' + key + ']', obj[key]);
            }
          } else {
            add(prefix, obj);
          }
        } else if (isArray(obj)) {
          for (i = 0, len = obj.length; i < len; i++) {
            add(obj[i].name, obj[i].value);
          }
        } else {
          for (key in obj) {
            buildParams(key, obj[key]);
          }
        }
        return s;
      };

    return buildParams('', a).join('&').replace(/%2C/g, ',');
  };

  const addFilter: AddFilterType = (
    filter: string,
    item: string,
    facet: FacetValue
  ) => {
    if (facet) {
      const updatedFacet = localStorage.getItem(filter)
        ? [...JSON.parse(localStorage.getItem(filter) as string), facet]
        : [facet];
      localStorage.setItem(filter, JSON.stringify(updatedFacet));
    }
    setSearchState(prevFields => ({
      ...prevFields,
      page: filterDefaults.page,
      selectedFilters: {
        ...prevFields.selectedFilters,
        [filter]: [
          ...Object.values(
            prevFields.selectedFilters[filter as ProviderFilterPanelsType]
          ),
          item,
        ],
      },
      selectedFacets: {
        ...prevFields.selectedFacets,
        [filter]: [
          ...Object.values(
            prevFields.selectedFacets[filter as ProviderFilterPanelsType]
          ),
          facet,
        ],
      },
    }));
  };

  const updateControlPanel: SetControlPanelType = (control: string) => {
    toggleMobileControls(control);
  };

  /* updateFilter is used when a single selection filter needs to be updated, multiple selection filters use addFilter */
  const updateFilter: UpdateFilterType = (
    filter: string,
    item: string,
    facet: FacetValue
  ) => {
    if (facet) {
      const updatedFacet = localStorage.getItem(filter)
        ? [...JSON.parse(localStorage.getItem(filter) as string), facet]
        : [facet];
      localStorage.setItem(filter, JSON.stringify(updatedFacet));
    }

    setSearchState(prevFields => ({
      ...prevFields,
      page: filterDefaults.page,
      selectedFilters: {
        ...prevFields.selectedFilters,
        [filter]:
          filterDefaults[filter as keyof ProviderDefaultsType] &&
          !Array.isArray(filterDefaults[filter as keyof ProviderDefaultsType])
            ? item
            : [item],
      },
      selectedFacets: {
        ...prevFields.selectedFacets,
        [filter]: [facet],
      },
      providerFilterPanels: searchState.providerFilterPanels.map(
        filterPanel => {
          return { ...filterPanel };
        }
      ),
    }));
  };

  const setActiveFilterPanel: SetActiveFilterPanelType = (id: string) => {
    const closingSearchFilter =
      searchState.activeFilterPanel === 'search' && id === '';

    setSearchState(prevFields => ({
      ...prevFields,
      activeFilterPanel: id,
      providerFilterPanels: searchState.providerFilterPanels.map(filterPanel =>
        filterPanel.id === id
          ? { ...filterPanel, active: true }
          : { ...filterPanel, active: false }
      ),
    }));

    closingSearchFilter && updateQuery(searchState.queryInputValue);
  };

  const updateLogoLinks = () => {
    const logoLinks = document.querySelectorAll('.logo-link');
    Array.from(logoLinks).forEach(logoLink => {
      if (logoLink?.getAttribute('href')?.startsWith('/providers')) {
        logoLink.setAttribute('href', `/providers${location.search}`);
      }
    });
  };

  /* replaceFilter allows for replacement of an entire selected filter's array */
  const replaceFilter: ReplaceFilterType = (
    filter: string,
    arr: Array<string>
  ) => {
    setSearchState(prevFields => ({
      ...prevFields,
      page: filterDefaults.page,
      selectedFilters: {
        ...prevFields.selectedFilters,
        [filter]: arr,
      },
    }));
  };

  const closeMobileControls = () => {
    document.body.classList.remove('mobile-controls-open');

    setSearchState(prevFields => ({
      ...prevFields,
      mobileControlsOpen: false,
    }));
  };

  const clearAllFilters = () => {
    setSearchState(prevFields => ({
      ...prevFields,
      query: '',
      queryInputValue: '',
      page: filterDefaults.page,
      sort: filterDefaults.sort,
      selectedFilters: {
        availability: filterDefaults.availability,
        acceptingnewpatients: filterDefaults.acceptingnewpatients,
        distance: filterDefaults.distance,
        gender: filterDefaults.gender,
        languages: filterDefaults.languages,
        latitude: '',
        location: '',
        longitude: '',
        locationnames: filterDefaults.locationnames,
        onlinescheduling: filterDefaults.onlinescheduling,
        sortfield: filterDefaults.sortfield,
        specscombo: filterDefaults.specscombo,
        PublishToLocal: filterDefaults.PublishToLocal,
      },
      selectedFacets: {
        acceptingnewpatients: filterDefaults.acceptingnewpatients,
        availability: filterDefaults.availability,
        gender: filterDefaults.gender,
        languages: filterDefaults.languages,
        locationnames: filterDefaults.locationnames,
        onlinescheduling: filterDefaults.onlinescheduling,
        sortfield: filterDefaults.sortfield,
        specscombo: filterDefaults.specscombo,
      },
    }));

    Object.keys(searchState.selectedFacets).forEach(facet => {
      if (localStorage.getItem(facet)) {
        localStorage.removeItem(facet);
      }
      delete lowercaseQuery[
        facet === 'specscombo' ? 'specialtyfilters' : facet
      ];
    });
  };

  const resetFilterToDefault: ResetFilterToDefaultType = (filter: string) => {
    setSearchState(prevFields => ({
      ...prevFields,
      page: filterDefaults.page,
      selectedFilters: {
        ...prevFields.selectedFilters,
        [filter]:
          filterDefaults[filter as keyof ProviderDefaultsType] ||
          filterDefaults[filter as keyof ProviderDefaultsType] === false
            ? filterDefaults[filter as keyof ProviderDefaultsType]
            : [],
      },
      selectedFacets: {
        ...prevFields.selectedFacets,
        [filter]:
          filterDefaults[filter as keyof ProviderDefaultsType] ||
          filterDefaults[filter as keyof ProviderDefaultsType] === false
            ? filterDefaults[filter as keyof ProviderDefaultsType]
            : [],
      },
    }));
  };

  const removeFilter: RemoveFilterType = (
    filter: string,
    facetName: string
  ) => {
    delete lowercaseQuery[
      filter === 'specscombo' ? 'specialtyfilters' : filter
    ];

    if (localStorage.getItem(filter)) {
      const updatedFacet = [
        ...JSON.parse(localStorage.getItem(filter) as string),
      ].filter(facet => facet.text !== facetName);
      updatedFacet.length == 0
        ? localStorage.removeItem(filter)
        : localStorage.setItem(filter, JSON.stringify(updatedFacet));
    }
    if (
      Array.isArray(filterDefaults[filter as keyof ProviderDefaultsType]) &&
      typeof filterDefaults[filter as keyof ProviderDefaultsType] !==
        'string' &&
      Object.values(
        searchState.selectedFilters[filter as ProviderFilterPanelsType]
      ).length > 1
    ) {
      setSearchState(prevFields => ({
        ...prevFields,
        page: filterDefaults.page,
        selectedFilters: {
          ...prevFields.selectedFilters,
          [filter]: Object.values(
            prevFields.selectedFilters[filter as ProviderFilterPanelsType]
          ).filter((e: string) => e !== facetName),
        },
        selectedFacets: {
          ...prevFields.selectedFacets,
          [filter]: Object.values(
            prevFields.selectedFacets[filter as ProviderFilterPanelsType]
          ).filter(
            (selectedFacet: { text: string }) =>
              selectedFacet.text !== facetName
          ),
        },
      }));
    } else {
      resetFilterToDefault(filter);
    }
  };

  const replaceLocationFilters = (
    lat: number,
    lng: number,
    location: string
  ) => {
    setSearchState(prevFields => ({
      ...prevFields,
      page: filterDefaults.page,
      sort: 'BEST_MATCH',
      selectedFilters: {
        ...prevFields.selectedFilters,
        latitude: lat,
        location: location,
        longitude: lng,
        distance: prevFields.selectedFilters.distance
          ? prevFields.selectedFilters.distance
          : '10',
      },
    }));
  };

  const removeLocationFilters = () => {
    setSearchState(prevFields => ({
      ...prevFields,
      page: filterDefaults.page,
      sort: filterDefaults.sort,
      selectedFilters: {
        ...prevFields.selectedFilters,
        distance: filterDefaults.distance,
        latitude: '',
        location: '',
        longitude: '',
      },
    }));
  };

  const updateQuery: UpdateQueryType = (str: string) => {
    setSearchState(state => ({
      ...state,
      activeFilterPanel: '',
      page: filterDefaults.page,
      query: str,
      queryInputValue: str,
      locationFilterPanels: state.locationFilterPanels.map(filterPanel =>
        filterPanel.id === 'search'
          ? { ...filterPanel, active: false }
          : { ...filterPanel }
      ),
    }));
  };

  const onQueryInputChange: OnQueryInputChangeType = (value: string) => {
    setSearchState(prevFields => ({
      ...prevFields,
      queryInputValue: value,
    }));
  };

  const getSelectedFilter = (filter: string) => {
    return lowercaseQuery[filter === 'specscombo' ? 'specialtyfilters' : filter]
      ? Array.isArray(filterDefaults[filter as keyof ProviderDefaultsType])
        ? lowercaseQuery[filter === 'specscombo' ? 'specialtyfilters' : filter]
            ?.toString()
            .split(/,(?! )/)
        : lowercaseQuery[filter === 'specscombo' ? 'specialtyfilters' : filter]
      : localStorage.getItem(filter)
        ? decodeURIComponent(localStorage.getItem(filter) || '')
        : filterDefaults[filter as keyof ProviderDefaultsType];
  };

  const getSelectedFacet = (filter: string) => {
    const currStorageVal = localStorage.getItem(filter);
    if (
      lowercaseQuery[filter === 'specscombo' ? 'specialtyfilters' : filter] &&
      (!currStorageVal ||
        (currStorageVal !== null && JSON.parse(currStorageVal).length == 0))
    ) {
      if (
        lowercaseQuery[
          filter === 'specscombo' ? 'specialtyfilters' : filter
        ]?.includes(',')
      ) {
        const values = lowercaseQuery[
          filter === 'specscombo' ? 'specialtyfilters' : filter
        ]
          ?.toString()
          .split(/,(?! )/);
        let selectedFacets = Array<FacetValue>();
        values?.forEach(val => {
          selectedFacets.push(
            allFacets
              .filter((facet: FacetType) => facet.name == filter)[0]
              ?.value.filter(
                (fv: FacetValue) => fv.text.toLowerCase() == val.toLowerCase()
              )[0]
          );
        });
        localStorage.setItem(filter, JSON.stringify(selectedFacets));
      } else {
        localStorage.setItem(
          filter,
          JSON.stringify(
            allFacets
              .filter((facet: FacetType) => facet.name == filter)[0]
              ?.value.filter(
                (fv: FacetValue) =>
                  fv.text.toLowerCase() ==
                  lowercaseQuery[
                    filter === 'specscombo' ? 'specialtyfilters' : filter
                  ]
                    ?.toString()
                    .toLowerCase()
              )
          )
        );
      }
    }

    return localStorage.getItem(filter)
      ? JSON.parse(localStorage.getItem(filter) as string)
      : lowercaseQuery[filter === 'specscombo' ? 'specialtyfilters' : filter]
        ? allFacets
            .filter((facet: FacetType) => facet.name == filter)[0]
            ?.value.filter(
              (fv: FacetValue) => fv.text == lowercaseQuery[filter]
            )
        : filterDefaults[filter as keyof ProviderDefaultsType];
  };

  //need to fix facet getting passed in here....why is english with specs?
  const getMoreFacets = async (facet: FacetType, searchQuery: SearchQuery) => {
    let facetResponse = null;
    let afterId = facet.value[facet.value.length - 1].id;
    const fullFacetList = facet.value;
    let moreFacets = true;

    const { content, ...newQuery } = searchQuery;
    const filteredFacet = newQuery.facet.types.filter(
      type => type.name !== facet.name && type.filter
    );

    while (moreFacets == true) {
      const typesArr: Array<TypeObject> = [
        {
          name: facet.name,
          sort: {
            name: 'text',
            order: 'asc',
          },
          after: afterId,
        },
      ];
      filteredFacet.length > 0 && typesArr.push(...filteredFacet);

      try {
        if (searchQuery) {
          const searchReq = {
            widget: {
              items: [
                {
                  rfk_id: getSearchWidget(),
                  entity: 'provider',
                  sources: [getProvidersSearchSrc()],
                  search: {
                    ...newQuery,
                    facet: {
                      all: false,
                      max: 100,
                      types: typesArr,
                    },
                  },
                },
              ],
            },
          };
          facetResponse = await Axios.post('/api/discovery', searchReq);
        } else {
          const searchReq = {
            widget: {
              items: [
                {
                  rfk_id: getSearchWidget(),
                  entity: 'provider',
                  sources: [getProvidersSearchSrc()],
                  search: {
                    facet: {
                      all: false,
                      max: 100,
                      types: [
                        {
                          name: facet.name,
                          sort: {
                            name: 'text',
                            order: 'asc',
                          },
                          after: afterId,
                        },
                      ],
                    },
                  },
                },
              ],
            },
          };
          facetResponse = await Axios.post('/api/discovery', searchReq);
        }

        if (facetResponse.data.widgets[0].facet) {
          const facets = facetResponse.data.widgets[0].facet.find(
            (facet: FacetType) => facet.name == facet.name
          ).value;
          fullFacetList.push(...facets);

          if (facets.length < 100) {
            moreFacets = false;
          } else {
            afterId = facets[facets.length - 1].id;
          }
        } else {
          moreFacets = false;
        }

        allFacets.map((f: FacetType) => {
          if (f.name == facet.name) {
            f.value = fullFacetList;
          }
        });
      } catch (error) {
        console.log('Failed to fetch facets. Error: ' + error);
      }
    }

    return allFacets.find(
      x => x.name.toLowerCase() === facet.name.toLowerCase()
    );
  };

  const search = async () => {
    const reqNum = ++currentReq.current;
    const { filters, query, selectedFilters } = searchState;

    let nonDefaultSelectedFilters: { [key: string]: string | Array<string> } =
      Object.keys(selectedFilters).reduce((nonDefaultSelectedFilters, key) => {
        if (
          selectedFilters[key as keyof ProviderFiltersType] ===
            filterDefaults[key as keyof ProviderDefaultsType] ||
          selectedFilters[key as keyof ProviderFiltersType] === null ||
          selectedFilters[key as keyof ProviderFiltersType] === '' ||
          JSON.stringify(selectedFilters[key as keyof ProviderFiltersType]) ===
            JSON.stringify([])
        ) {
          return nonDefaultSelectedFilters;
        }
        return {
          ...nonDefaultSelectedFilters,
          [key]: Array.isArray(
            selectedFilters[key as keyof ProviderFiltersType]
          )
            ? Object.values(
                selectedFilters[key as ProviderFilterPanelsType]
              ).map((val: string | Array<string> | boolean) => val.toString())
            : selectedFilters[key as keyof ProviderFiltersType],
        };
      }, {});

    setSearchState(prevFields => ({
      ...prevFields,
      loading: true,
      nonDefaultSelectedFilters: nonDefaultSelectedFilters,
    }));

    // map to graphQL ProviderFiltersInput schema
    delete nonDefaultSelectedFilters['locationnames'];
    if (selectedFilters?.locationnames?.length > 0) {
      nonDefaultSelectedFilters['locationnames'] =
        selectedFilters.locationnames;
    }

    if (allFacetsReady) {
      try {
        let response;

        //Creates array of types to pass to query facet
        let typesArray: Array<FacetQueryType> = [];
        //filtersArray restricts facets returned to all behave as AND conditions to the selected options, so commented out it and its usages except for geolocation
        let filtersArray: Array<{}> = [];
        if (
          Object.keys(nonDefaultSelectedFilters).filter(
            key => key !== 'distance'
          ).length > 0
        ) {
          Object.keys(searchState.selectedFacets).forEach(key => {
            if (
              searchState.selectedFacets[key].length > 0 ||
              Object.keys(searchState.selectedFacets[key]).length > 0
            ) {
              if (key == 'specscombo' || key == 'locationnames') {
                let typeObject = {
                  name: key,
                  max: 100,
                  filter: {
                    type: 'or',
                    values: Array.isArray(searchState.selectedFacets[key])
                      ? searchState.selectedFacets[key].map(
                          (facet: { id: any }) => facet.id
                        )
                      : [searchState.selectedFacets[key].id],
                  },
                  sort: {
                    name: 'text',
                    order: 'asc',
                  },
                };
                typesArray.push(typeObject);

                // let filterObject = {};
                // if (Array.isArray(searchState.selectedFacets[key])) {
                //   searchState.selectedFacets[key].forEach(
                //     (val: { text: string }) => {
                //       filterObject = {
                //         name: key,
                //         type: 'eq',
                //         value: val.text,
                //       };
                //       filtersArray.push(filterObject);
                //     }
                //   );
                // } else {
                //   filterObject = {
                //     name: key,
                //     type: 'eq',
                //     value: searchState.selectedFacets[key].text,
                //   };
                //   filtersArray.push(filterObject);
                // }
              } else {
                let typeObject = {
                  name: key,
                  max: 100,
                  filter: {
                    type: 'or',
                    values: Array.isArray(searchState.selectedFacets[key])
                      ? searchState.selectedFacets[key].map(
                          (facet: { id: any }) => facet.id
                        )
                      : [searchState.selectedFacets[key].id],
                  },
                };
                typesArray.push(typeObject);

                // let filterObject = {};
                // if (Array.isArray(searchState.selectedFacets[key])) {
                //   searchState.selectedFacets[key].forEach(
                //     (val: { text: string }) => {
                //       filterObject = {
                //         name: key,
                //         type: 'eq',
                //         value: val.text,
                //       };
                //       filtersArray.push(filterObject);
                //     }
                //   );
                // } else {
                //   filterObject = {
                //     name: key,
                //     type: 'eq',
                //     value: searchState.selectedFacets[key].text,
                //   };
                //   filtersArray.push(filterObject);
                // }
              }
            }
          });
        }
        if (!Object.keys(nonDefaultSelectedFilters).includes('specscombo')) {
          typesArray.push({
            name: 'specscombo', // Only this facet will be sorted
            max: 100,
            sort: {
              name: 'text',
              order: 'asc',
            },
          });
        }

        if (!Object.keys(nonDefaultSelectedFilters).includes('locationnames')) {
          typesArray.push({
            name: 'locationnames', // Only this facet will be sorted
            max: 100,
            sort: {
              name: 'text',
              order: 'asc',
            },
          });
        }

        let defaultSearchBody: any = {
          content: {},
          limit: searchState.pageSize,
          offset: (searchState.page - 1) * searchState.pageSize,
          facet: {
            all: true,
            max: 100,
            types: [
              // Only these facets will be sorted
              {
                name: 'specscombo',
                sort: {
                  name: 'text',
                  order: 'asc',
                },
              },
              {
                name: 'locationnames',
                sort: {
                  name: 'text',
                  order: 'asc',
                },
              },
            ],
          },
          rule: {
            boost: [
              {
                weight: 2,
                filter: {
                  type: 'exists',
                  name: 'image',
                },
              },
              {
                weight: 3.5,
                filter: {
                  type: 'eq',
                  name: 'sortfield',
                  value: 'Employed',
                },
              },
              {
                weight: 2,
                filter: {
                  type: 'or',
                  filters: [
                    {
                      type: 'eq',
                      name: 'specscombo',
                      value: 'Internal Medicine',
                    },
                    {
                      type: 'eq',
                      name: 'specscombo',
                      value: 'Pediatrics',
                    },
                    {
                      type: 'eq',
                      name: 'specscombo',
                      value: 'Family Medicine',
                    },
                  ],
                },
              },
              {
                weight: 1.5,
                filter: {
                  type: 'eq',
                  name: 'sortfield',
                  value: 'Contracted',
                },
              },
            ],
            bury: {
              filter: {
                type: 'or',
                filters: [
                  {
                    type: 'eq',
                    name: 'sortfield',
                    value: 'Other',
                  },
                  {
                    type: 'eq',
                    name: 'specscombo',
                    value: 'Anesthesiology',
                  },
                  {
                    type: 'eq',
                    name: 'specscombo',
                    value: 'Emergency Medicine',
                  },
                ],
              },
            },
          },
        };

        if (searchState.selectedFilters.location) {
          filtersArray.push({
            type: 'geoDistance',
            name: 'locationscoordinates',
            distance: `${searchState.selectedFilters.distance}km`,
            //setting to users entered location (searched or from geolocation)
            lat: parseFloat(searchState.selectedFilters.latitude as string),
            lon: parseFloat(searchState.selectedFilters.longitude as string),
          });
          defaultSearchBody = {
            ...defaultSearchBody,
            filter: {
              type: 'and',
              filters: filtersArray,
            },
          };
        }

        //best match is same as no sort
        let geoContext = {};

        if (searchState.sort && searchState.sort !== 'BEST_MATCH') {
          if (searchState.sort == 'DISTANCE') {
            geoContext = {
              context: {
                geo: {
                  location: {
                    lat: parseFloat(
                      searchState.selectedFilters.latitude as string
                    ),
                    lon: parseFloat(
                      searchState.selectedFilters.longitude as string
                    ),
                  },
                },
              },
            };
            defaultSearchBody = {
              ...defaultSearchBody,
              sort: {
                value: [
                  {
                    name: 'coordinates',
                    order: 'asc',
                  },
                ],
              },
            };
          } else {
            const sortValue =
              searchState.sort == 'REVERSE_ALPHABETICAL'
                ? 'lastName_descending'
                : 'lastName_ascending';
            defaultSearchBody = {
              ...defaultSearchBody,
              sort: {
                value: [
                  {
                    name: sortValue,
                  },
                ],
                choices: false,
              },
            };
          }
        }

        if (query.length > 0) {
          defaultSearchBody = {
            ...defaultSearchBody,
            query: {
              keyphrase: query,
              operator: 'and',
            },
          };
        }

        if (
          Object.entries(searchState.selectedFacets)
            .filter(
              ([key]) =>
                key !== 'location' &&
                key !== 'latitude' &&
                key !== 'longitude' &&
                key !== 'distance'
            )
            .filter(([_, value]) => (value as Array<any>).length > 0).length >
            0 ||
          Object.entries(searchState.selectedFacets)
            .filter(([key]) => key == 'availability')
            .filter(([_, value]) => Object.keys(value as {}).length > 0)
            .length > 0
        ) {
          defaultSearchBody = {
            ...defaultSearchBody,
            // filter: {
            //   type: 'and',
            //   filters: filtersArray,
            // },
            facet: {
              all: true,
              max: 100,
              types: typesArray,
            },
          };
        }

        const searchReq = {
          ...geoContext,
          widget: {
            items: [
              {
                rfk_id: getSearchWidget(),
                entity: 'provider',
                sources: [getProvidersSearchSrc()],
                search: defaultSearchBody,
              },
            ],
          },
        };
        response = await Axios.post('/api/discovery', searchReq);

        const data = response.data;

        const expandedFacets = await Promise.all(
          data.widgets[0]?.facet?.map(async (f: FacetType) => {
            if (f.value.length >= 100) {
              return await getMoreFacets(f, defaultSearchBody);
            }
            return f;
          }) || []
        );

        if (reqNum == currentReq.current) {
          setSearchState(prevFields => ({
            ...prevFields,
            apiError: false,
            results: data.widgets[0].content,
            count: data.widgets[0].total_item,
            loading: false,
            facets:
              expandedFacets.length > 0
                ? expandedFacets
                : data.widgets[0].facet,
            filters: {
              ...filters,
              acceptingnewpatients:
                expandedFacets.find(
                  (obj: DataFacet) => obj.name == 'acceptingnewpatients'
                )?.value ||
                data.widgets[0].facet?.find(
                  (obj: DataFacet) => obj.name == 'acceptingnewpatients'
                )?.value,
              availability: data.widgets[0].facet?.find(
                (obj: DataFacet) => obj.name == 'availability'
              )?.value,
              gender: data.widgets[0].facet?.find(
                (obj: DataFacet) => obj.name == 'gender'
              )?.value,
              locationnames: data.widgets[0].facet?.find(
                (obj: DataFacet) => obj.name == 'locationnames'
              )?.value,
              onlinescheduling: data.widgets[0].facet?.find(
                (obj: DataFacet) => obj.name == 'onlinescheduling'
              )?.value,
              specscombo: data.widgets[0].facet?.find(
                (obj: DataFacet) => obj.name == 'specscombo'
              )?.value,
              languages: data.widgets[0].facet?.find(
                (obj: DataFacet) => obj.name == 'languages'
              )?.value,
            },
          }));
        }
      } catch (error) {
        setSearchState(prevFields => ({
          ...prevFields,
          apiError: true,
          count: 0,
          loading: false,
          results: [],
        }));
      }
    }
  };

  const setLatLng = (latLng: LatLng, zip?: string) => {
    setSearchState(prevState => ({
      ...prevState,
      selectedFilters: {
        ...prevState.selectedFilters,
        latitude: latLng.lat,
        longitude: latLng.lng,
        distance: prevState.selectedFilters.distance
          ? prevState.selectedFilters.distance
          : '10',
      },
    }));

    if (!zip) {
      reverseGeocode(latLng);
    }
  };

  const buildAddress = (address: NormalizedAddress, latLng?: LatLng) => {
    const locationString =
      address.label ||
      `${address.city ? `${address.city}, ` : ''}${
        address.state ? `${address.state} ` : ''
      }${address.zip ?? ''}`.trim();

    setSearchState(prevFields => ({
      ...prevFields,
      selectedFilters: {
        ...prevFields.selectedFilters,
        location: locationString,
        latitude: latLng?.lat || '',
        longitude: latLng?.lng || '',
      },
    }));

    if (latLng) {
      replaceLocationFilters(latLng.lat, latLng.lng, locationString);
    }
  };

  const reverseGeocode = async (latLng: LatLng, setAddress?: Function) => {
    const subscriptionKey = process.env.NEXT_PUBLIC_AZURE_MAPS_SUBSCRIPTION_KEY;

    if (!subscriptionKey) {
      setSearchState(prevState => ({
        ...prevState,
        geolocating: false,
      }));

      return;
    }

    try {
      const response = await Axios.get(
        'https://atlas.microsoft.com/reverseGeocode',
        {
          params: {
            'api-version': '2026-01-01',
            coordinates: `${latLng.lng},${latLng.lat}`,
            'subscription-key': subscriptionKey,
            language: 'en-US',
          },
        }
      );

      const result = response.data;

      const address: NormalizedAddress = {
        city:
          result?.features?.[0]?.properties?.address?.locality ||
          result?.features?.[0]?.properties?.address?.municipality,
        state:
          result?.features?.[0]?.properties?.address?.adminDistricts?.[0]
            ?.shortName ||
          result?.features?.[0]?.properties?.address?.adminDistricts?.[0]
            ?.name ||
          result?.features?.[0]?.properties?.address?.countrySubdivision,
        zip: result?.features?.[0]?.properties?.address?.postalCode,
      };

      setAddress && setAddress(address);

      setSearchState(prevState => ({
        ...prevState,
        geolocating: false,
      }));

      buildAddress(address, latLng);
    } catch (error) {
      setSearchState(prevState => ({
        ...prevState,
        geolocating: false,
      }));
    }
  };

  const geolocate = (setAddress?: Function) => {
    setSearchState(prevFields => ({
      ...prevFields,
      geolocating: true,
    }));

    navigator.geolocation.getCurrentPosition(
      position => {
        if (
          position &&
          position.coords &&
          position.coords.latitude &&
          position.coords.longitude
        ) {
          const latLng: LatLng = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          reverseGeocode(latLng, setAddress);
        }
      },
      () => {
        setSearchState(prevState => ({
          ...prevState,
          geolocating: false,
        }));
      }
    );
  };

  const updateSort = (sort: string) => {
    setSearchState(prevFields => ({
      ...prevFields,
      sort: sort,
      page: filterDefaults.page,
    }));
  };

  const openMobileControls = (control: string) => {
    if (!(getOsfViewportSize() === 'xl' || getOsfViewportSize() === 'xxl')) {
      document.body.classList.add('mobile-controls-open');

      setSearchState(prevFields => ({
        ...prevFields,
        controlPanel: control,
        mobileControlsOpen: true,
      }));
    }
  };

  const toggleMobileControls: ToggleMobileControlsType = (control: string) => {
    if (searchState.mobileControlsOpen) {
      closeMobileControls();
    } else {
      openMobileControls(control);
    }
  };

  const setDownloadVisability = () => {
    //Shows or hides the modal shown on download button based on number of providers
    const count = searchState.count;
    const downloadBtns = document.querySelectorAll('.download-btn');
    const modalBtns = document.querySelectorAll('.modal-btn');
    if (window.location.search.length <= 1) {
      Array.from(downloadBtns).forEach(downloadBtn => {
        downloadBtn.classList.add('hidden');
      });
      Array.from(modalBtns).forEach(modalBtn => {
        modalBtn.classList.add('hidden');
      });
    } else if (count >= 200) {
      Array.from(downloadBtns).forEach(downloadBtn => {
        downloadBtn.classList.add('hidden');
      });
      Array.from(modalBtns).forEach(modalBtn => {
        modalBtn.classList.remove('hidden');
      });
    } else {
      Array.from(downloadBtns).forEach(downloadBtn => {
        downloadBtn.classList.remove('hidden');
      });
      Array.from(modalBtns).forEach(modalBtn => {
        modalBtn.classList.add('hidden');
      });
    }
  };

  const getResultsCount = () => {
    const { count, pageSize, page } = searchState;

    setDownloadVisability();

    return getResultsCountText({
      count,
      pageSize,
      page,
      searchType: 'providers',
    });
  };

  const updatePageSize = (pageSize: number) => {
    setSearchState(prevFields => ({
      ...prevFields,
      pageSize: pageSize,
      page: filterDefaults.page,
    }));
  };

  const updatePage = (page: number) => {
    setSearchState(prevFields => ({
      ...prevFields,
      page: page,
    }));
    document
      .querySelector('.search-page')
      ?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchFirstFacets = async () => {
    try {
      const searchReq = {
        widget: {
          items: [
            {
              rfk_id: getSearchWidget(),
              entity: 'provider',
              sources: [getProvidersSearchSrc()],
              search: {
                facet: {
                  all: true,
                  max: 100,
                  types: [
                    // Only these facets will be sorted
                    {
                      name: 'specscombo',
                      sort: {
                        name: 'text',
                        order: 'asc',
                      },
                    },
                    {
                      name: 'locationnames',
                      sort: {
                        name: 'text',
                        order: 'asc',
                      },
                    },
                  ],
                },
              },
            },
          ],
        },
      };
      const response = await Axios.post('/api/discovery', searchReq);
      return response.data.widgets[0].facet;
    } catch (error) {
      console.log('Failed to fetch facets. Error: ' + error);
    }
  };

  const fetchRemainingFacets = async (facetName: string, afterId: string) => {
    try {
      const searchReq = {
        widget: {
          items: [
            {
              rfk_id: getSearchWidget(),
              entity: 'provider',
              sources: [getProvidersSearchSrc()],
              search: {
                facet: {
                  all: false,
                  max: 100,
                  types: [
                    {
                      name: facetName,
                      sort: {
                        name: 'text',
                        order: 'asc',
                      },
                      after: afterId,
                    },
                  ],
                },
              },
            },
          ],
        },
      };
      const facetResponse = await Axios.post('/api/discovery', searchReq);
      return (
        facetResponse.data.widgets[0].facet.find(
          (f: FacetType) => f.name === facetName
        )?.value || []
      );
    } catch (error) {
      console.log('Failed to fetch remaining facets. Error: ' + error);
    }
  };

  const fetchAllFacets = async () => {
    const firstFacets = await fetchFirstFacets();
    for (const facet of firstFacets) {
      const allValues = [...facet.value];

      if (facet.value.length >= 100) {
        let afterId = facet.value[facet.value.length - 1].id;
        let hasMore = true;

        while (hasMore) {
          const nextValues = await fetchRemainingFacets(facet.name, afterId);

          allValues.push(...nextValues);

          if (nextValues.length < 100) {
            hasMore = false;
          } else {
            afterId = nextValues[nextValues.length - 1].id;
          }
        }

        facet.value = allValues;
      }
    }
    return firstFacets;
  };

  const fetchSuperSpecialties = async () => {
    try {
      const superSpecialtiesQuery = gql`
        query ($superSpecialtiesID: String!) {
          search(
            where: {
              AND: [
                {
                  name: "_parent"
                  value: $superSpecialtiesID
                  operator: CONTAINS
                }
              ]
            }
          ) {
            results {
              id
              name
              Icon: field(name: "Icon") {
                value: value
              }
            }
          }
        }
      `;

      const superSpecialtiesID = '{98915AD7-950E-4B3A-9510-BAFEC7C9D50B}';

      const { data } = await Axios.post('/api/delivery', {
        query: superSpecialtiesQuery,
        variables: {
          superSpecialtiesID: superSpecialtiesID,
        },
      });

      setSuperSpecialties(data.data.search.results);
    } catch (error) {
      console.log('Failed to fetch facets. Error: ' + error);
    }
  };

  useEffect(() => {
    fetchSuperSpecialties();
    const load = async () => {
      const data = await fetchAllFacets();
      setAllFacets(data);
      setAllFacetsReady(true);
    };
    load();

    Object.keys(searchState.selectedFacets).forEach(facet => {
      if (
        !lowercaseQuery[facet] ||
        lowercaseQuery[facet] !== localStorage.getItem(facet)
      ) {
        localStorage.removeItem(facet);
      }
    });

    // Set viewport-dependent values after component mounts
    const viewportSize = getOsfViewportSize();
    if (Object.keys(lowercaseQuery).length > 1) {
      setSearchState(prevFields => ({
        ...prevFields,
        controlPanel:
          viewportSize === 'lg' || viewportSize === 'xl' ? 'filters' : '',
        locationsPageSize:
          viewportSize === 'lg' || viewportSize === 'xl' ? 1000 : 10,
        selectedFilters: {
          availability: getSelectedFilter('availability') as Array<string>,
          acceptingnewpatients: getSelectedFilter(
            'acceptingnewpatients'
          ) as Array<string>,
          distance: getSelectedFilter('distance') as string,
          gender: getSelectedFilter('gender') as Array<string>,
          languages: getSelectedFilter('languages') as Array<string>,
          latitude: getSelectedFilter('latitude') as string | number,
          location: getSelectedFilter('location') as string,
          longitude: getSelectedFilter('longitude') as string | number,
          locationnames: getSelectedFilter('locationnames') as Array<string>,
          onlinescheduling: getSelectedFilter(
            'onlinescheduling'
          ) as Array<string>,
          sortfield: getSelectedFilter('sortfield') as Array<string>,
          specscombo: getSelectedFilter('specscombo') as Array<string>,
          PublishToLocal: getSelectedFilter('PublishToLocal') as string,
        },
        query: (lowercaseQuery['query'] as string) || filterDefaults.query,
        queryInputValue:
          (lowercaseQuery['query'] as string) || filterDefaults.query,
        page: parseInt(lowercaseQuery['page'] as string) || filterDefaults.page,
      }));
    }
  }, []);

  useEffect(() => {
    allFacetsReady &&
      setSearchState(prevFields => ({
        ...prevFields,
        selectedFacets: {
          acceptingnewpatients: getSelectedFacet(
            'acceptingnewpatients'
          ) as Array<string>,
          availability: getSelectedFacet('availability') as Array<string>,
          gender: getSelectedFacet('gender') as Array<string>,
          languages: getSelectedFacet('languages') as Array<string>,
          locationnames: getSelectedFacet('locationnames') as Array<string>,
          onlinescheduling: getSelectedFacet(
            'onlinescheduling'
          ) as Array<string>,
          sortfield: getSelectedFacet('sortfield') as Array<string>,
          specscombo: getSelectedFacet('specscombo') as Array<string>,
        },
      }));
  }, [allFacetsReady]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlStateParams = {
        query: searchState.query,
        page: searchState.page === 1 ? null : searchState.page,
        sort: searchState.selectedFilters.location
          ? searchState.sort === 'DISTANCE'
            ? null
            : searchState.sort
          : searchState.sort === filterDefaults.sort
            ? null
            : searchState.sort,
        pageSize:
          searchState.pageSize === pageSizeOptions[0]
            ? null
            : searchState.pageSize,
        ...searchState.selectedFilters,
      };

      let nonDefaultParams: NonDefaultParams = Object.keys(
        urlStateParams
      ).reduce((nonDefaultParams, key) => {
        if (searchState.selectedFilters.location && key !== 'distance') {
          if (
            urlStateParams[key as keyof ProviderFiltersType] ===
              filterDefaults[key as keyof ProviderDefaultsType] ||
            urlStateParams[key as keyof ProviderFiltersType] === null ||
            urlStateParams[key as keyof ProviderFiltersType] == ''
          )
            return nonDefaultParams;
        } else if (!searchState.selectedFilters.location) {
          if (
            urlStateParams[key as keyof ProviderFiltersType] ===
              filterDefaults[key as keyof ProviderDefaultsType] ||
            urlStateParams[key as keyof ProviderFiltersType] === null ||
            urlStateParams[key as keyof ProviderFiltersType] == ''
          )
            return nonDefaultParams;
        }
        return {
          ...nonDefaultParams,
          //Switches specscombo filter key name to show as specialtyfilters in the url
          [key === 'specscombo' ? 'specialtyfilters' : key]:
            urlStateParams[key as keyof ProviderFiltersType]?.toString(),
        };
      }, {});

      //filter out keys that aren't in defaults
      let nonFilterParams: any = Object.fromEntries(
        Object.entries(lowercaseQuery).filter(
          ([key]) =>
            key !== 'path' &&
            !Object.keys(filterDefaults).includes(
              key === 'specialtyfilters' ? 'specscombo' : key
            ) &&
            !Object.keys(urlStateParams).includes(
              key === 'specialtyfilters' ? 'specscombo' : key
            )
        )
      );

      const qp = { ...nonDefaultParams, ...nonFilterParams };
      const testParam = param(qp);
      const newQuery = { ...lowercaseQuery };

      Object.keys(newQuery).map(key => {
        if (
          key !== 'path' &&
          (nonDefaultParams[key] ==
            filterDefaults[key as keyof ProviderDefaultsType] ||
            !nonDefaultParams[key])
        ) {
          delete newQuery[key];
        }
        if (
          searchState.selectedFilters[key as keyof ProviderFiltersType] !==
            undefined &&
          searchState.selectedFilters[key as keyof ProviderFiltersType] ===
            filterDefaults[key as keyof ProviderDefaultsType]
        ) {
          delete newQuery[key];
        }
      });

      if (testParam.length > 0) {
        router.push(
          {
            pathname: router.pathname,
            query: { ...newQuery, ...nonDefaultParams, ...nonFilterParams },
          },
          undefined,
          { shallow: true }
        );
      } else {
        router.push(
          {
            pathname: router.pathname,
            query: { ...newQuery, ...nonDefaultParams, ...nonFilterParams },
          },
          undefined,
          { shallow: true }
        );
      }

      if (
        Object.keys(urlStateParams).length !== 0 &&
        !(Object.keys(urlStateParams).length === 1)
      ) {
        urlStateParams.location = searchState.selectedFilters.location;
      }

      searchState.selectedFilters.longitude
        ? localStorage.setItem(
            'longitude',
            searchState.selectedFilters.longitude.toString()
          )
        : delete localStorage.longitude;

      searchState.selectedFilters.latitude
        ? localStorage.setItem(
            'latitude',
            searchState.selectedFilters.latitude.toString()
          )
        : delete localStorage.latitude;

      searchState.selectedFilters.distance
        ? localStorage.setItem(
            'distance',
            searchState.selectedFilters.distance.toString()
          )
        : delete localStorage.distance;

      searchState.selectedFilters.location
        ? localStorage.setItem('location', searchState.selectedFilters.location)
        : delete localStorage.location;

      updateLogoLinks();
    }
    search();
  }, [
    searchState.selectedFilters,
    searchState.selectedFacets,
    searchState.query,
    searchState.sort,
    searchState.page,
    searchState.pageSize,
  ]);

  useEffect(() => {
    if (window.location.search) {
      let storedName = window.location.search;
      if (window.location.search.includes('specialtyfilters')) {
        storedName = window.location.search.replace(
          'specialtyfilters',
          'specscombo'
        );
      }
      localStorage.setItem('providerSearch', storedName);

      const pageParam = Object.fromEntries(
        new URLSearchParams(localStorage.providerSearch)
      ).page;

      setSearchState(prevFields => ({
        ...prevFields,
        page: parseInt(pageParam) || filterDefaults.page,
      }));
    } else {
      const params = Object.fromEntries(
        new URLSearchParams(localStorage.providerSearch)
      );
      Object.keys(params).map(key => {
        if (Object.keys(initialState.selectedFilters).includes(key)) {
          setSearchState(prevFields => ({
            ...prevFields,
            // selectedFacets: {
            //   ...prevFields.selectedFacets,
            //   [key]: filterDefaults[key as keyof ProviderDefaultsType] || '',
            // },
            // selectedFilters: {
            //   ...prevFields.selectedFilters,
            //   [key]: filterDefaults[key as keyof ProviderDefaultsType] || '',
            // },
            page: filterDefaults.page,
          }));
        } else if (key === 'page') {
          setSearchState(prevFields => ({
            ...prevFields,
            page: filterDefaults.page,
          }));
        }
      });
      delete localStorage.providerSearch;
    }
  }, [routerQuery]);

  useEffect(() => {
    const resize = () => {
      if (getOsfViewportSize() === 'xl' || getOsfViewportSize() === 'xxl') {
        searchState.mobileControlsOpen && closeMobileControls();
      }
    };
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
    };
  }, [searchState.mobileControlsOpen]);

  return (
    <div className="search-page overhauled">
      <HeroComponent
        rendering={{ uid: 'provider-search', componentName: 'HeroComponent' }}
        fields={{
          lowerLevelHero: { value: true },
          BaseImage: {
            value: {
              src: Constants.greenHeroImage,
              width: 4096,
              height: 1755,
            },
          },
          BaseTitle: { value: 'Find a Provider' },
          MarginSize: { fields: { heading: { value: '' } } },
        }}
      ></HeroComponent>
      <SearchBar
        clearAllFilters={clearAllFilters}
        filterPanels={searchState.providerFilterPanels}
        setActiveFilterPanel={setActiveFilterPanel}
        activeFilterPanel={searchState.activeFilterPanel}
        filters={searchState.filters}
        selectedFacets={searchState.selectedFacets}
        selectedFilters={searchState.selectedFilters}
        addFilter={addFilter}
        updateFilter={updateFilter}
        replaceFilter={replaceFilter}
        buildAddress={buildAddress}
        geolocate={geolocate}
        geolocating={searchState.geolocating}
        replaceLocationFilters={replaceLocationFilters}
        removeLocationFilters={removeLocationFilters}
        removeFilter={removeFilter}
        setLatLng={setLatLng}
        query={searchState.query}
        queryInputValue={searchState.queryInputValue}
        onQueryInputChange={onQueryInputChange}
        updateQuery={updateQuery}
        closeMobileControls={closeMobileControls}
        resultsCount={searchState.count}
        searchType={searchType}
      ></SearchBar>
      <div className="jss-inner-container">
        <>
          <div className="search-page-controls providers only-mobile">
            <h2 className="screen-recording-only" tabIndex={0}>
              Filters
            </h2>
            <ControlToggles
              selectedFacetsCount={selectedFacetsCount}
              searchType={searchType}
              updateControlPanel={updateControlPanel}
              onQueryInputChange={onQueryInputChange}
              updateQuery={updateQuery}
              queryInputValue={searchState.queryInputValue}
              sort={searchState.sort}
              updateSort={updateSort}
              withOrigin={searchState.selectedFilters.location ? true : false}
              selectedFilters={searchState.selectedFilters}
            />
          </div>
          <div className="search-page-wrapper providers">
            <ResultsControls
              loading={searchState.loading}
              count={searchState.count}
              getResultsCount={getResultsCount}
              updateSort={updateSort}
              activeFilterPanel={searchState.activeFilterPanel}
              removeFilter={removeFilter}
              removeLocationFilters={removeLocationFilters}
              filters={searchState.filters}
              filterDefaults={filterDefaults}
              query={searchState.query}
              updateQuery={updateQuery}
              selectedFilters={searchState.selectedFilters}
              selectedFacets={searchState.selectedFacets}
              selectedFacetsCount={selectedFacetsCount}
              searchType={'providers'}
              sort={searchState.sort}
              withOrigin={searchState.selectedFilters.location ? true : false}
              clearAllFilters={clearAllFilters}
            />
            <div className="left-col">
              <div
                className="bg-overlay"
                onClick={e => {
                  if (searchState.mobileControlsOpen) {
                    const targ = e.target as HTMLElement;
                    !targ.classList.contains('.filter-bar-wrapper') &&
                      !targ.closest('.filter-bar-wrapper') &&
                      updateControlPanel('filters');
                  }
                }}
              >
                <ProviderFilters
                  clearAllFilters={clearAllFilters}
                  filterPanels={searchState.providerFilterPanels}
                  setActiveFilterPanel={setActiveFilterPanel}
                  activeFilterPanel={searchState.activeFilterPanel}
                  filters={searchState.filters}
                  selectedFacets={searchState.selectedFacets}
                  selectedFacetsCount={selectedFacetsCount}
                  selectedFilters={searchState.selectedFilters}
                  addFilter={addFilter}
                  updateFilter={updateFilter}
                  replaceFilter={replaceFilter}
                  buildAddress={buildAddress}
                  geolocate={geolocate}
                  geolocating={searchState.geolocating}
                  replaceLocationFilters={replaceLocationFilters}
                  removeLocationFilters={removeLocationFilters}
                  removeFilter={removeFilter}
                  setLatLng={setLatLng}
                  query={searchState.query}
                  queryInputValue={searchState.queryInputValue}
                  onQueryInputChange={onQueryInputChange}
                  updateQuery={updateQuery}
                  closeMobileControls={closeMobileControls}
                  resultsCount={searchState.count}
                  superSpecialties={superSpecialties}
                />
              </div>
              <div className="search-page-results">
                {Array.isArray(searchState.results) &&
                searchState.results.length > 0 ? (
                  <ProvidersSearchResults
                    apiError={searchState.apiError}
                    clearAllFilters={clearAllFilters}
                    count={searchState.count}
                    origin={{
                      latitude: searchState.selectedFilters.latitude,
                      longitude: searchState.selectedFilters.longitude,
                    }}
                    pageSizeOptions={pageSizeOptions}
                    pageSize={searchState.pageSize}
                    results={searchState.results}
                    searchType={searchType}
                    page={searchState.page}
                    updatePageSize={updatePageSize}
                    updatePage={updatePage}
                    loading={searchState.loading}
                  />
                ) : searchState.loading ? (
                  <ResultsSkeleton />
                ) : (
                  <ProvidersSearchResults
                    apiError={searchState.apiError}
                    clearAllFilters={clearAllFilters}
                    count={searchState.count}
                    origin={{
                      latitude: searchState.selectedFilters.latitude,
                      longitude: searchState.selectedFilters.longitude,
                    }}
                    pageSizeOptions={pageSizeOptions}
                    pageSize={searchState.pageSize}
                    results={[]}
                    searchType={searchType}
                    page={searchState.page}
                    updatePageSize={updatePageSize}
                    updatePage={updatePage}
                    loading={false}
                  />
                )}
              </div>
            </div>
            <div id="modal-root" />
          </div>
        </>
      </div>
    </div>
  );
};

export default ProviderSearch;
